import AmountInput from 'components/AmountInput'
import CustomButton from 'components/Buttons/CustomButton'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Trash2, X } from 'react-feather'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  Progress,
} from 'reactstrap'
import {
  usePostPayReceivableMutation,
  usePostPayReceivablePersonalMutation,
} from '../receivable-notes/receivableNotesApiSlice'
import { toggleSidebarReceivable } from '../receivable-notes/receivableNotesSlice'
import {
  useLazyGetPayableQuery,
  usePostPayablePersonalMutation,
  usePostPayPayableMutation,
  usePostPayPayablePersonalMutation,
} from './payableNotesApiSlice'
import { toggleSidebar } from './payableNotesSlice'
import SvgIcon from 'components/Icon/SvgIcon'
import usePartialUpload from 'hooks/usePartialUpload'
import LoadingAnimation from 'components/LoadingAnimation'

const ModalAdjustPayable = ({
  row,
  isReceivable,
  toggle,
  isOpen,
  refetch,
  clearSelectedRows,
}) => {
  const [localRows, setLocalRows] = useState(row)
  const total = localRows?.reduce((acc, row) => {
    const amount = row?.amount ?? 0
    const paid = row?.amountPaid ?? 0
    return acc + (amount - paid)
  }, 0)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const intl = useIntl()
  const { type } = useSelector((s) => s.payableNotes)
  const f = isReceivable ? 'receivable' : 'payable'

  const [isUserEditingTotal, setIsUserEditingTotal] = useState(false)
  const [step, setStep] = useState(1)
  const [paymentMethod, setPaymentMethod] = useState(0)
  const [amounts, setAmounts] = useState({})
  const [totalAdjust, setTotalAdjust] = useState(total ?? '')
  const [postPayPayableCompany, resultCompany] = usePostPayPayableMutation()
  const [postPayPayablePersonal, resultPersonal] =
    usePostPayPayablePersonalMutation()
  const [postPayReceivableCompany, resultReceivableCompany] =
    usePostPayReceivableMutation()
  const [postPayReceivablePersonal, resultReceivablePersonal] =
    usePostPayReceivablePersonalMutation()

  const postPayPayable =
    type === 'personal' ? postPayPayablePersonal : postPayPayableCompany
  const postPayReceivable =
    type === 'personal' ? postPayReceivablePersonal : postPayReceivableCompany
  const result = type === 'personal' ? resultPersonal : resultCompany
  const resultReceivable =
    type === 'personal' ? resultReceivablePersonal : resultReceivableCompany

  const { uploadFile } = usePartialUpload()
  const [getPayable] = useLazyGetPayableQuery()
  const [note, setNote] = useState('')
  const [fileNames, setFileNames] = useState([])
  const [files, setFiles] = useState([])
  const { enqueueSnackbar } = useSnackbar()

  const sortedRows = useMemo(() => {
    return [...localRows].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    )
  }, [localRows])

  const handleAmountChange = (id, val) => {
    setIsUserEditingTotal(true)

    setAmounts((prev) => {
      const updated = { ...prev, [id]: val }

      const sum = Object.values(updated).reduce((acc, v) => {
        const num = Number(String(v).replace(/\./g, '').replace(/,/g, '.'))
        return acc + (isNaN(num) ? 0 : num)
      }, 0)

      setTotalAdjust(
        intl.formatNumber(sum, {
          useGrouping: 'always',
          minimumFractionDigits: 0,
          maximumFractionDigits: 5,
        }),
      )
      return updated
    })
  }

  const handleDelete = (id) => {
    const updatedRows = localRows.filter((e) => e.id !== id)

    setLocalRows(updatedRows)

    const newTotal = updatedRows.reduce((acc, r) => {
      const val = amounts[r?.id] ?? '0'
      const num = Number(val.replace(/\./g, '').replace(/,/g, '.'))
      return acc + num
    }, 0)

    setTotalAdjust(
      intl.formatNumber(newTotal, {
        useGrouping: 'always',
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
      }),
    )
  }

  const handleSubmit = async () => {
    try {
      const cleanedTotal = Number(
        String(totalAdjust).replace(/\./g, '').replace(/,/g, '.'),
      )

      const itemsFormatted = localRows.map((e) => ({
        payableId: e.id,
        amountPaid: Number(
          (amounts?.[e?.id] || '0').replace(/\./g, '').replace(/,/g, '.'),
        ),
        manual: e?.escrowpaymentId ? 0 : 1,
        ...(isReceivable
          ? { payerName: e.payerName ?? e?.buyerName ?? '' }
          : { receivableName: e.name ?? e?.sellerName ?? '' }),
      }))

      const obj = isReceivable
        ? {
            totalAmount: cleanedTotal,
            note,
            totalItem: itemsFormatted.length,
            item: itemsFormatted,
          }
        : {
            totalAmount: cleanedTotal,
            note,
            paymentMethod:
              paymentMethod === 0
                ? 'uangbelanja'
                : paymentMethod === 1
                  ? 'paymentviaagent'
                  : 'cashpayment',
            files: files?.length > 0 ? files?.map((f) => f.fileUrl) : [],
            totalItem: itemsFormatted.length,
            item: itemsFormatted,
          }

      const resp = isReceivable
        ? await postPayReceivable(obj).unwrap()
        : await postPayPayable(obj).unwrap()

      if (!resp?.data?.error) {
        if (clearSelectedRows) {
          clearSelectedRows()
        }
        toggle()
        enqueueSnackbar(
          isReceivable
            ? 'Penyesuaian piutang berhasil.'
            : 'Penyesuaian tagihan berhasil.',
          { variant: 'success' },
        )
        refetch()
      } else {
        throw resp
      }
    } catch (error) {
      const errorMessage =
        typeof error === 'string'
          ? error
          : (error?.data?.message ??
            error?.data?.error ??
            error?.message ??
            'Terjadi kesalahan')

      enqueueSnackbar(errorMessage, {
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    setNote('')
    setFileNames('')
    setFiles('')
    setPaymentMethod(0)
    setStep(1)
  }, [isOpen])

  useEffect(() => {
    setLocalRows(row)
    const initialTotal = row?.reduce((acc, row) => {
      const amount = row?.amount ?? 0
      const paid = row?.amountPaid ?? 0
      return acc + (amount - paid)
    }, 0)

    setTotalAdjust(
      intl.formatNumber(initialTotal, {
        useGrouping: 'always',
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
      }),
    )
  }, [row, isOpen])

  useEffect(() => {
    if (isUserEditingTotal) return
    if (!totalAdjust) return

    let sisa =
      typeof totalAdjust === 'string'
        ? Number(totalAdjust?.replace(/\./g, '')?.replace(/,/g, '.') ?? 0)
        : totalAdjust

    const newAmounts = {}

    for (const r of sortedRows) {
      const remaining = (r?.amount ?? 0) - (r?.amountPaid ?? 0)

      if (sisa <= 0) {
        newAmounts[r?.id] = '0'
        continue
      }

      const pakai = Math.min(sisa, remaining)

      newAmounts[r?.id] = String(pakai)

      sisa -= pakai
    }

    setAmounts(newAmounts)
  }, [totalAdjust, sortedRows, isUserEditingTotal])

  return (
    <Modal
      centered
      isOpen={isOpen}
      toggle={() => {
        toggle()
      }}
      autoFocus={false}
      size="lg"
      data-testid={`${f}-modal-adjust`}
    >
      <ModalBody>
        <h5>Sesuaikan {isReceivable ? 'Piutang' : 'Utang'}</h5>
        <div className="d-flex justify-content-between mb-4 mt-4">
          <div className="d-flex flex-column">
            <div>
              <span className="fw-500">
                {isReceivable ? 'Piutang' : 'Utang'} Terpilih:{' '}
              </span>
              <span>{localRows?.length > 0 ? localRows?.length : 1}</span>
            </div>
            <div>
              <span className="fw-500">
                Maksimum {isReceivable ? 'Piutang' : 'Utang'}:{' '}
              </span>
              <span>
                Rp{' '}
                {intl.formatNumber(total ?? 0, {
                  useGrouping: 'always',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
          </div>
          <div>
            <span className="fw-500">Jumlah Total Penyesuaian</span>
            <InputGroup>
              <InputGroupText className="bg-transparent">Rp</InputGroupText>
              <AmountInput
                value={totalAdjust}
                max={total ?? 0}
                cb={(res) => {
                  setTotalAdjust(res)
                  setIsUserEditingTotal(false)
                }}
                placeholder="0"
                disabled={step === 2}
                maxFractionDigits={2}
              />
            </InputGroup>
          </div>
        </div>

        {step === 1 ? (
          <div>
            <div className="border rounded mb-4 p-2 d-flex align-items-start gap-2">
              <small>
                <AlertCircle size={15} className="me-1" />
                <small>
                  Penyesuaian dialokasikan menggunakan metode FIFO (First In,
                  First Out). Faktur terlama akan diselesaikan terlebih dahulu.
                  Anda dapat menyesuaikan jumlah masing-masing secara manual di
                  bawah ini.
                </small>
              </small>
            </div>

            {sortedRows?.length > 0
              ? sortedRows?.map((e, i) => (
                  <Card key={e?.id ?? i} className="border-0 shadow-sm mb-3">
                    <CardBody>
                      <FormGroup>
                        <div className="d-flex flex-wrap justify-content-between align-items-center">
                          <Label className="d-flex align-items-center">
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: '#e9ecef',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                marginRight: 8,
                              }}
                            >
                              {i + 1}
                            </div>

                            {/* nama */}
                            <div className="d-flex flex-column">
                              <span className="fw-500">
                                {!e?.userId ||
                                !e?.payerUserId ||
                                !e?.escrowpaymentId
                                  ? isReceivable
                                    ? e?.payerName || e?.buyerName || e?.buyer?.name || '-'
                                    : e?.name || e?.sellerName || e?.seller?.name || e?.payeeName || '-'
                                  : isReceivable
                                    ? e?.buyerName || e?.payerName || e?.buyer?.name || '-'
                                    : e?.sellerName || e?.name || e?.seller?.name || e?.payeeName || '-'}
                              </span>
                              {(isReceivable
                                ? e?.buyerPartnershipName || e?.buyer?.partnership?.name
                                : e?.sellerPartnershipName || e?.seller?.partnership?.name) && (
                                <span className="text-muted" style={{ fontSize: '12px' }}>
                                  {isReceivable
                                    ? e?.buyerPartnershipName || e?.buyer?.partnership?.name
                                    : e?.sellerPartnershipName || e?.seller?.partnership?.name}
                                </span>
                              )}
                            </div>
                          </Label>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="text-gray-600 d-flex align-items-center">
                              Sisa: Rp{' '}
                              {intl.formatNumber(
                                (e?.amount ?? 0) - (e?.amountPaid ?? 0) ?? 0,
                                {
                                  useGrouping: 'always',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                },
                              )}
                            </div>
                          </div>
                        </div>

                        <InputGroup>
                          <InputGroupText className="bg-transparent">
                            Rp
                          </InputGroupText>
                          <AmountInput
                            max={(e?.amount ?? 0) - (e?.amountPaid ?? 0)}
                            value={amounts?.[e?.id] ?? ''}
                            cb={(res) => handleAmountChange(e.id, res)}
                            placeholder="0"
                            maxFractionDigits={2}
                          />
                        </InputGroup>

                        {Number(
                          amounts?.[e?.id]
                            ?.replace?.(/\./g, '')
                            ?.replace?.(/,/g, '.')
                            ?.slice?.(0, 15),
                        ) === 0 && (
                          <div className="text-danger mt-1">
                            <AlertCircle size={15} className="me-1" />
                            <small>Nominal tidak boleh 0.</small>
                          </div>
                        )}

                        <Progress
                          value={
                            (e?.amount ?? 0) - (e?.amountPaid ?? 0) > 0
                              ? (Number(
                                  amounts?.[e?.id]
                                    ?.replace?.(/\./g, '')
                                    ?.replace?.(/,/g, '.')
                                    ?.slice?.(0, 15),
                                ) /
                                  ((e?.amount ?? 0) - (e?.amountPaid ?? 0))) *
                                100
                              : 0
                          }
                          className="mt-3"
                        />

                        <div className="mt-1 text-gray-600 text-center">
                          {`${((e?.amount ?? 0) - (e?.amountPaid ?? 0) > 0
                            ? (Number(
                                amounts?.[e?.id]
                                  ?.replace(/\./g, '')
                                  ?.replace(/,/g, '.')
                                  ?.slice(0, 15),
                              ) /
                                ((e?.amount ?? 0) - (e?.amountPaid ?? 0))) *
                              100
                            : 0
                          ).toFixed(
                            0,
                          )}% dari sisa ${isReceivable ? 'piutang' : 'utang'}`}
                        </div>
                      </FormGroup>

                      <div className="d-flex justify-content-end">
                        <CustomButton
                          color="danger"
                          size="sm"
                          onClick={() => handleDelete(e.id)}
                        >
                          <Trash2 size={15} /> Hapus
                        </CustomButton>
                      </div>
                    </CardBody>
                  </Card>
                ))
              : null}
          </div>
        ) : (
          <div>
            {isReceivable || !localRows?.[0]?.escrowpaymentId ? (
              <></>
            ) : (
              <>
                <Card
                  onClick={() => {
                    setPaymentMethod(0)
                  }}
                  className={`mb-2 pointer ${paymentMethod === 0 ? 'bg-primary-50' : ''}`}
                >
                  <CardBody>
                    <div className="d-flex justify-content-between">
                      <div className="d-flex gap-2 align-items-center">
                        <SvgIcon
                          name="wallet"
                          size={30}
                          color="white"
                          withBackground={true}
                          backgroundColor="#006386"
                        />
                        Uang Belanja
                      </div>
                      <Input
                        type="checkbox"
                        checked={paymentMethod === 0}
                        onChange={() => {
                          setPaymentMethod(0)
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
                <Card
                  onClick={() => {
                    // Disabled temporarily
                  }}
                  className={`mb-2 pointer ${paymentMethod === 1 ? 'bg-primary-50' : ''}`}
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  <CardBody>
                    <div className="d-flex justify-content-between">
                      <div className="d-flex gap-2 align-items-center">
                        <SvgIcon
                          name="user"
                          size={30}
                          color="black"
                          withBackground={true}
                          backgroundColor="#FEDF89"
                        />
                        Bayar Melalui Agen
                      </div>
                      <Input
                        type="checkbox"
                        checked={paymentMethod === 1}
                        onChange={() => {
                          // Disabled temporarily
                        }}
                        disabled={true}
                      />
                    </div>
                  </CardBody>
                </Card>
                <Card
                  onClick={() => {
                    setPaymentMethod(2)
                  }}
                  className={`mb-2 pointer ${paymentMethod === 2 ? 'bg-primary-50' : ''}`}
                >
                  <CardBody>
                    <div className="d-flex justify-content-between">
                      <div className="d-flex gap-2 align-items-center">
                        <SvgIcon
                          name="old-icons-money-bill-wave"
                          size={30}
                          color="black"
                          withBackground={true}
                        />
                        Uang Tunai
                      </div>
                      <Input
                        type="checkbox"
                        checked={paymentMethod === 2}
                        onChange={() => {
                          setPaymentMethod(2)
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
                {paymentMethod === 2 && (
                  <>
                    <FormGroup>
                      <Label>Bukti Bayar</Label>
                      <Form
                        encType="multipart/form-data"
                        className="flex-centered border rounded"
                        style={{ overflow: 'hidden' }}
                      >
                        <fieldset className="fieldset">
                          <input
                            className="input-file"
                            id="transactions"
                            disabled={isLoading}
                            name="transactions"
                            onChange={async (e) => {
                              setIsLoading(true)
                              try {
                                const newFiles = e.target.files
                                let newFileNames = []
                                let newUploadedFiles = []

                                for (let i = 0; i < newFiles.length; i++) {
                                  const uploadedFile = await uploadFile(
                                    newFiles[i],
                                    () => {},
                                    'FILE',
                                    'EVENT',
                                    true,
                                  )

                                  if (!uploadedFile?.url) {
                                    enqueueSnackbar(
                                      uploadedFile?.payload || 'Upload gagal',
                                      {
                                        variant: 'error',
                                      },
                                    )
                                  } else {
                                    const fileId = Date.now() + i
                                    newFileNames.push({
                                      id: fileId,
                                      fileName: newFiles[i].name,
                                    })
                                    newUploadedFiles.push({
                                      id: fileId,
                                      fileUrl: uploadedFile?.url,
                                    })
                                  }
                                }

                                setFileNames((prev) => [
                                  ...prev,
                                  ...newFileNames,
                                ])
                                setFiles((prev) => [
                                  ...prev,
                                  ...newUploadedFiles,
                                ])
                              } catch (err) {
                                enqueueSnackbar(
                                  'Terjadi kesalahan saat upload file',
                                  {
                                    variant: 'error',
                                  },
                                )
                              } finally {
                                setIsLoading(false)
                              }
                            }}
                            type="file"
                            accept=".png, .jpeg, .jpg, .pdf"
                            multiple
                          />
                          <label htmlFor="transactions" className="d-flex">
                            {isLoading ? <LoadingAnimation /> : 'Upload'}
                          </label>
                        </fieldset>
                        <div className="w-100" style={{ padding: '10px 14px' }}>
                          {fileNames?.length > 0 && !isLoading
                            ? fileNames?.map((e, i) => (
                                <span className="ms-3 badge-line" key={i}>
                                  {e.fileName}
                                  <X
                                    size={16}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      const filteredFileNames =
                                        fileNames.filter(
                                          (file) => file.id !== e.id,
                                        )
                                      const filteredFiles = files?.filter(
                                        (file) => file.id !== e.id,
                                      )
                                      setFileNames(filteredFileNames)
                                      setFiles((prev) => [
                                        ...prev,
                                        ...filteredFiles,
                                      ])
                                    }}
                                  />
                                </span>
                              ))
                            : 'Belum ada file'}
                        </div>
                      </Form>
                    </FormGroup>
                  </>
                )}
              </>
            )}

            <FormGroup className="mt-3">
              <Label className="fw-500">Catatan</Label>
              <Input
                id="noteText"
                name="text"
                type="textarea"
                placeholder="Tambahkan catatan jika ada..."
                onChange={(e) => {
                  setNote(e?.target?.value)
                }}
                value={note}
              />
            </FormGroup>
          </div>
        )}

        <div className="d-flex gap-3 mt-4">
          <CustomButton
            onClick={() => {
              step === 1 ? toggle() : setStep(1)
            }}
            disabled={result?.isLoading || resultReceivable?.isLoading}
            color="primary"
            outline
            block
            className="btn-cancel"
            testId={`${f}-modal-adjust-cancel`}
          >
            {step === 1 ? 'Batal' : 'Kembali'}
          </CustomButton>
          <CustomButton
            disabled={
              !amounts ||
              result.isLoading ||
              resultReceivable?.isLoading ||
              (paymentMethod === 2 && files?.length < 1) ||
              Object.values(amounts).some((a) => {
                const num = Number(
                  String(a).replace(/\./g, '').replace(/,/g, '.'),
                )
                return !num || num === 0
              })
            }
            onClick={() => {
              if (step === 1) {
                setStep(2)
              } else {
                handleSubmit()
              }
            }}
            color="primary"
            block
            testId={`${f}-modal-adjust-submit`}
          >
            {step === 1 && !isReceivable ? 'Lanjutkan' : 'Simpan'}
          </CustomButton>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ModalAdjustPayable
